/**
 * The permission ids (D14; PLAN §11): users → groups → `PermissionIdWire[]`, each
 * group with a scope. The single list every route, authorizer, group editor
 * and offline check draws from — a plain exported const, so the cloud
 * foundations (tenancy, groups, the authorizer) and the rundown API import the
 * same ids.
 *
 * Add an id here, in the same change as the first route or check that needs
 * it. Ids are never renamed: they are stored in every group record, cloud and
 * node alike.
 */
import { openEnum } from './openEnum.js';
import { z } from 'zod';

export const PERMISSION_IDS = [
  /** Manage the tenant: users, groups, sites, studios. */
  'tenant.admin',
  /** See billing and usage. */
  'billing.view',
  /** Read rundowns and playlists and their rows. */
  'rundown.read',
  /** Create, edit, reorder and (soft) delete rundowns, playlists and their rows. */
  'rundown.edit',
  /** Take, set next, adlib; confirm on-air updates of live-editable fields (D19). */
  'playout.take',
  /** Activate, deactivate and reset a playlist. */
  'playout.activate',
  /** Launch a production on an on-prem node. */
  'production.launch.onprem',
  /** Launch a cloud production (D10). */
  'production.launch.cloud',
  /** Switch a production (or a whole node) between main and backup (D9). */
  'node.switchover',
  /** Configure studios: devices, mappings, piece types. */
  'device.configure',
  /** Break-glass offline login on a node while it cannot reach the cloud (D20). */
  'local.login',
  /** Cross-tenant vendor operations dashboard (D27). */
  'vendor.ops',
  /** Cross-tenant vendor administration: create and list tenants (D13, D27). */
  'vendor.admin',
  /**
   * Staff support (D39): `vendor.ops` plus read-only tenant content and diagnostics. Reserved in phase 7f (grantable in
   * the staff tenant, implied by the levels above it); phase 7g gives it routes.
   */
  'vendor.support',
  /** Staff support actions (D39): `vendor.support` plus node, user and release care. Reserved in 7f, like `vendor.support`. */
  'vendor.support.act',
  /** Read the tenant's graphics library: projects, content, settings (ACP-D45, GFX-D107). */
  'graphics.read',
  /** Create, save and delete graphics projects and upload their content. */
  'graphics.edit',
  /** Publish graphics template versions (wave 4 of M1 gives it routes; granted by the default groups now). */
  'graphics.publish',
  /** Administer the tenant's graphics settings: the aspect-class set. */
  'graphics.admin',
  /** Open breakout sessions: the designer's preview mirrored on other screens through a capability link (ACP-D47). */
  'graphics.breakout',
  /**
   * Open and control playout control sessions: renderers driven through the relay, holding its control lease (ACP-D52,
   * gfx/docs/plans/playout.md Q5). Watching a session needs only `graphics.read`.
   */
  'graphics.playout',
  /** Add a published template version to Mimir as a new graphics item (ACP-D48, docs/gfx-mimir-publish/plan.md). */
  'graphics.mimir',
] as const;

/** `vendor.*` permissions are grantable only inside the vendor's own tenant (D27). */
export function isVendorPermission(permission: PermissionIdWire): boolean {
  return permission.startsWith('vendor.');
}

/**
 * D39's staff levels, lowest first: `vendor.ops` ⊂ `vendor.support` ⊂ `vendor.support.act` ⊂ `vendor.admin`. Holding
 * one holds every level below it (superadmin = `vendor.admin` + `tenant.admin` of the staff tenant).
 */
export const VENDOR_LEVELS = ['vendor.ops', 'vendor.support', 'vendor.support.act', 'vendor.admin'] as const satisfies readonly PermissionIdWire[];
export type VendorLevelWire = (typeof VENDOR_LEVELS)[number];

function isVendorLevel(permission: PermissionIdWire): permission is VendorLevelWire {
  return VENDOR_LEVELS.some((level) => level === permission);
}

/**
 * The permissions a held list amounts to: itself, and every staff level below each staff level it holds (D39's
 * hierarchy). The cloud expands a caller's grants with it (`effectiveGrants`), so one `isPermitted` check of
 * `vendor.ops` admits a `vendor.admin` too. Sorted, without duplicates.
 */
export function withImpliedLevels(permissions: readonly PermissionIdWire[]): PermissionIdWire[] {
  const out = new Set<PermissionIdWire>(permissions);
  for (const p of permissions) {
    if (!isVendorLevel(p)) continue;
    for (const lower of VENDOR_LEVELS.slice(0, VENDOR_LEVELS.indexOf(p))) out.add(lower);
  }
  return [...out].sort();
}

/** Closed: a permission a request grants (a group's create or update), and what the cloud itself checks. */
export const permissionIdWireSchema = z.enum(PERMISSION_IDS);
export type PermissionIdWire = z.infer<typeof permissionIdWireSchema>;
/**
 * Open (D37 §4.3): a permission as a reader receives it (a group's list, `GET /me`) — a newer cloud may grant one
 * this reader does not know. An `unknown` permission grants nothing a reader can check (`knownPermissions`), and an
 * editor holding one must not save the group's permissions back (it would erase it).
 */
export const permissionIdOpenWireSchema = openEnum(PERMISSION_IDS);
type PermissionIdOpenWire = z.infer<typeof permissionIdOpenWireSchema>;

/** The permissions a reader knows, of a list it received: an `unknown` one grants nothing it can check. */
export function knownPermissions(permissions: readonly PermissionIdOpenWire[]): PermissionIdWire[] {
  return permissions.filter((p): p is PermissionIdWire => p !== 'unknown');
}

/** Whether `value` is a known permission id. */
export function isPermissionId(value: string): value is PermissionIdWire {
  return permissionIdWireSchema.safeParse(value).success;
}
